import { cx } from '@emotion/css';
import React, { EventHandler, useState } from 'react';
import { SizeVariants } from '../../../theme/themes';
import { makeCSS, WithTheme } from '../../../utils/makeCSS';
import { Icon, IconName } from '../../atoms/Icon';
import { iconFiles } from '../../atoms/Icon/files';
import { Key, KeyProps } from '../../atoms/Key';
import { Typography } from '../../atoms/Typography';
import { isMobile } from 'react-device-detect';
import MaybeTooltip, {
  MaybeTooltipProps
} from '../../../../../ui/src/Tooltip/MaybeTooltip';
import mergeRefs from '../../../helpers/MergeRefs';
import { logoFiles } from '../../atoms/BrandLogo/files';
import { BrandLogo, LogoName } from '../../atoms/BrandLogo/BrandLogo';
/**
 * The ContextMenuListItem is displaying one of the options that the user can choose
 * from in the ContextMenu. A number of different ContextMenuListItem components and ContextMenuDivider components compose the final ContextMenu.
 */

export interface ContextMenuListItemProps extends KeyProps, MaybeTooltipProps {
  leftIcon?: keyof typeof iconFiles | keyof typeof logoFiles | React.ReactNode;
  rightIcon?: keyof typeof iconFiles | JSX.Element;
  /**The label of this option.*/
  name?: string;
  /** the form value of the item when used in a Select component */
  value?: string;
  /**Display / Use a modifier to indicate the shortcut for this option.*/
  showShortcut?: boolean;
  modifiers?: string;
  /**Display or not use a chevron for this option.*/
  showChevron?: boolean;
  /**Select this option, name it to `onClick` here to prevent it gets override in `Select` component */
  onClick?: EventHandler<React.MouseEvent>;
  /**Select this option, name it to `onClick` here to prevent it gets override in `Select` component */
  onSelect?: EventHandler<React.SyntheticEvent>;
  /**Displayed this option */
  disabled?: boolean;
  dataType?: 'librarydeselectoverride' | 'none';
  // /**Extra props that get applied to the root container */
  extraRootProps?: {
    component?: string | React.ElementType;
    href?: string;
    download?: boolean;
    datatype?: string;
    [key: string]: any;
  };
  /**color variant*/
  color?: '01' | '02';
  disableHover?: boolean;
  isActive?: boolean;
}

const useCSS = makeCSS(
  ({ theme, disableHover = false }: WithTheme<ContextMenuListItemProps>) => {
    return {
      root: {
        background: 'none',
        userSelect: 'none',
        display: 'inline-flex',
        width: '100%',
        minHeight: theme.spacing[10],
        padding: theme.spacing[2],
        alignItems: 'center',
        border: 'none',
        borderRadius: theme.borderRadius.lg,
        cursor: 'pointer',
        outline: 'none',
        boxSizing: 'border-box',
        '&:focus-visible': {
          outline: `1px solid ${theme.color.foreground.primary}`,
          boxShadow: `0px 0px 0px 3px rgba(255, 212, 35, 0.12)`
        },
        '&:hover': {
          background: disableHover ? 'transparent' : theme.color.overlay.hovered
        },
        '&:active': {
          background: isMobile ? 'none' : theme.color.overlay.pressed
        },
        '&.disabled': {
          cursor: 'default',
          opacity: 0.4,
          '&:hover': {
            background: 'none'
          }
        }
      },
      mobileSize: {
        height: theme.spacing[14]
      },
      labelStyle: {
        pointerEvents: 'none',
        marginRight: theme.spacing[2],
        textWrap: 'nowrap'
      },
      leftIcon: {
        color: theme.color.icon['02'],
        paddingRight: theme.spacing[2],
        pointerEvents: 'none'
      },
      rightIcon: {
        color: theme.color.icon['02'],
        paddingLeft: theme.spacing[2],
        pointerEvents: 'none'
      },
      alignRight: {
        marginLeft: 'auto'
      },
      modifiers: {
        marginLeft: 'auto',
        pointerEvents: 'none'
      },
      active: {
        background: theme.color.overlay.pressed
      }
    };
  }
);

export function ContextMenuListItem(props: ContextMenuListItemProps) {
  const {
    leftIcon,
    value,
    name,
    showShortcut = true,
    modifiers = '',
    showChevron = true,
    onClick,
    onSelect,
    disabled,
    rightIcon,
    modifierSize = SizeVariants.md,
    dataType,
    disabledReason,
    disabledReasonPlacement = 'bottom',
    extraRootProps = { buttonRef: null },
    color = '01',
    disableHover,
    isActive
  } = props;

  const { buttonRef } = extraRootProps;

  const classes = useCSS({ disableHover });

  const size = isMobile ? 'lg' : 'md';

  //icon color
  const [focus, setFocus] = useState(false);
  const colorVariant =
    color === '01' ? (focus ? '01' : '02') : focus ? '02' : '03';

  const handleFocus = () => {
    disabled ? setFocus(false) : setFocus(true);
  };
  const handleBlur = () => {
    setFocus(false);
  };

  const addHoverStyle = () => {
    if (!disabled) {
      setFocus(true);
    }
  };
  const removeHoverStyle = () => {
    setFocus(false);
  };

  const displayName = name || value?.charAt(0).toUpperCase() + value?.slice(1);

  return (
    <MaybeTooltip
      disabled={!disabled && !disabledReason}
      title={disabled && disabledReason}
      placement={disabledReasonPlacement}
    >
      <button
        data-type={dataType}
        onFocus={handleFocus}
        onBlur={handleBlur}
        tabIndex={0}
        onClick={disabled ? null : onClick || onSelect}
        onMouseOver={addHoverStyle}
        onMouseLeave={removeHoverStyle}
        className={cx(
          classes.root,
          {
            disabled: disabled
          },
          isMobile ? classes.mobileSize : '',
          isActive ? classes.active : ''
        )}
        ref={mergeRefs(buttonRef)}
        {...extraRootProps}
      >
        {leftIcon && (
          <div className={cx(classes.leftIcon)}>
            {(leftIcon as IconName) in iconFiles ? (
              <Icon
                icon={leftIcon as IconName}
                colorVariant={colorVariant}
                size={SizeVariants[`${size}`]}
              />
            ) : (
              <BrandLogo
                logo={leftIcon as LogoName}
                size={SizeVariants[`${size}`]}
              />
            )}
          </div>
        )}
        <div className={`${classes.labelStyle}`} role="option">
          <Typography
            variant={`${size}`}
            colorVariant={colorVariant}
            weight="body"
          >
            {/**capitalize first letter */}
            {displayName}
          </Typography>
        </div>
        {showShortcut && modifiers !== '' ? (
          <>
            {!isMobile && (
              <div className={`${classes.modifiers}`}>
                <Key
                  keyCode={modifiers}
                  variant="modifier"
                  modifierSize={modifierSize}
                />
              </div>
            )}
          </>
        ) : (
          ''
        )}
        {showChevron && (
          <div
            className={cx(
              classes.rightIcon,
              !showShortcut || !modifiers ? classes.alignRight : ''
            )}
          >
            <Icon
              icon="chevron_right"
              colorVariant={colorVariant}
              size={SizeVariants[`${size}`]}
            />
          </div>
        )}
        {rightIcon &&
          (typeof rightIcon === 'string' ? (
            <div className={cx(classes.rightIcon, classes.alignRight)}>
              <Icon
                icon={rightIcon}
                colorVariant={colorVariant}
                size={SizeVariants[`${size}`]}
              />
            </div>
          ) : (
            <div className={classes.alignRight}>{rightIcon}</div>
          ))}
      </button>
    </MaybeTooltip>
  );
}
